import { addMonths, endOfMonth, format, startOfMonth, subMonths, UTCDate } from '@/utils/dates';
import { AccountView } from '../../../../types/entity-types';
import { SupportedCountryCurrency } from '../../../../types/supported-country-currency';
import { MetricCard } from '../../components/metrics/metric-card';
import { getCurrentCashData } from '../../utils/get-current-cash-data';
import { getEstimatedRunwayData } from '../../utils/get-estimated-runway-data';
import { getMonthlyBurnData } from '../../utils/get-monthly-burn-data';
import { getRevenueData } from '../../utils/get-revenue-data';

type CashFlowMetricsProps = {
  accounts: AccountView[];
  baseCurrency: SupportedCountryCurrency;
};

const CashFlowMetrics = ({ accounts, baseCurrency }: CashFlowMetricsProps) => {
  //TODO: Implement a timeframe prop from the filter
  const now = new UTCDate();
  const beginningOfMonth = startOfMonth(now);
  const startOfPreviousMonth = startOfMonth(subMonths(now, 1));
  const endOfPreviousMonth = endOfMonth(subMonths(now, 1));

  const currentCashData = getCurrentCashData({
    accounts,
    baseCurrency,
    startDate: beginningOfMonth,
    endDate: now,
  });

  const previousMonthBurnData = getMonthlyBurnData({
    accounts,
    baseCurrency,
    currentPeriodStartDate: startOfPreviousMonth,
    currentPeriodEndDate: endOfPreviousMonth,
    previousPeriodStartDate: startOfMonth(subMonths(now, 2)),
    previousPeriodEndDate: endOfMonth(subMonths(now, 2)),
  });

  const estimatedRunwayData = getEstimatedRunwayData({
    accounts,
    baseCurrency,
    currentPeriodStartDate: beginningOfMonth,
    currentPeriodEndDate: now,
    previousMonthStartDate: startOfPreviousMonth,
    previousMonthEndDate: endOfPreviousMonth,
  });

  const revenueData = getRevenueData({
    accounts,
    baseCurrency,
    currentPeriodStartDate: beginningOfMonth,
    currentPeriodEndDate: now,
    previousPeriodStartDate: startOfPreviousMonth,
    previousPeriodEndDate: endOfPreviousMonth,
  });

  const metrics = [
    {
      label: 'Current Cash',
      value: currentCashData.formattedCurrentCash,
      trend: currentCashData.percentageChange,
      direction: currentCashData.trend,
      subText: `from last month`,
      valueTone: currentCashData.valueTone,
    },
    {
      label: 'Monthly burn',
      value: previousMonthBurnData.formattedMonthlyBurn,
      trend: previousMonthBurnData.percentageChange,
      direction: previousMonthBurnData.trend,
      subText: `from ${format(subMonths(now, 2), 'MMMM')}`,
      valueTone: previousMonthBurnData.valueTone,
      tooltipText: <p className="text-xs">Gross burn: last calendar month's total spend excluding revenue</p>,
    },
    {
      label: 'Estimated Runway',
      value: estimatedRunwayData.formattedRunway,
      trend: estimatedRunwayData.percentageChange,
      direction: estimatedRunwayData.trend,
      subText: `from last month`,
      valueTone: estimatedRunwayData.valueTone,
      tooltipText: (
        <p className="text-xs">
          {Number.isFinite(estimatedRunwayData.currentRunwayInMonths)
            ? `Until ${format(addMonths(now, Math.round(estimatedRunwayData.currentRunwayInMonths)), "MMM ''yy")}`
            : '∞'}
        </p>
      ),
    },
    {
      label: 'Revenue',
      value: revenueData.formattedRevenue,
      trend: revenueData.percentageChange,
      direction: revenueData.trend,
      subText: `from last month`,
      valueTone: revenueData.valueTone,
    },
  ];

  return (
    <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {metrics.map((metric) => (
        <MetricCard
          key={metric.label}
          label={metric.label}
          value={metric.value}
          trend={metric.trend}
          direction={metric.direction}
          subText={metric.subText}
          valueTone={metric.valueTone}
          tooltipText={metric.tooltipText}
        />
      ))}
    </div>
  );
};

export default CashFlowMetrics;
